/**
 * OCS Gallery Zoom
 *
 * Creates an overlay <img> whose layout size IS the zoomed size (scale × container).
 * The browser rasterizes it at full pixel dimensions using the high-res source.
 * Panning uses translate() only — no CSS scale() — so there is no compositor
 * layer upscaling and zoom is crisp on every click, including the first.
 *
 * All overlay element styles are set inline via JS to prevent base.css rules
 * (`.product-media-container img { aspect-ratio: inherit; object-fit: contain }`)
 * from interfering with the overlay image.
 */
(function () {
  'use strict';

  function initGalleryZoom() {
    var containers = document.querySelectorAll('.product-media-container--ocs-zoomable');
    if (!containers.length) return;

    containers.forEach(function (container) {
      var img = container.querySelector('.product-media__image');
      if (!img) return;

      // Shopify image_tag sets src to the 3840w URL; fall back to data-max-resolution.
      var highResSrc = img.getAttribute('src') || img.getAttribute('data-max-resolution') || '';
      if (!highResSrc) return;

      var zoomed      = false;
      var pending     = false;
      var activeScale = 2.5; // cached at zoom-in time, reused for every mousemove
      var touchStartX = 0;
      var touchStartY = 0;
      var touchMoved = false;
      var suppressClickUntil = 0;

      // ── Ensure container is a positioning context ────────────────────────
      container.style.position = 'relative';

      // ── Build overlay (all styles inline to beat theme CSS) ──────────────
      var overlay = document.createElement('div');
      overlay.style.cssText = 'position:absolute;inset:0;overflow:hidden;display:none;pointer-events:none;z-index:1';

      var overlayImg = document.createElement('img');
      // Inline styles override base.css `.product-media-container img` rules:
      // aspect-ratio:auto  → cancels `aspect-ratio: inherit`
      // max-width/height   → prevents accidental shrinkage
      overlayImg.style.cssText = 'position:absolute;top:0;left:0;aspect-ratio:auto;max-width:none;max-height:none;will-change:transform';
      overlayImg.setAttribute('alt', '');
      overlayImg.setAttribute('aria-hidden', 'true');

      overlay.appendChild(overlayImg);
      container.appendChild(overlay);

      // ── Preload high-res on first hover ───────────────────────────────────
      var preloadImg = null;
      container.addEventListener('mouseenter', function () {
        if (preloadImg) return;
        preloadImg = new Image();
        preloadImg.src = highResSrc;
      });

      // ── Panning ───────────────────────────────────────────────────────────
      // Overlay img starts at top:0 left:0 and is (scale × 100%) in each axis.
      // To keep the point under the cursor fixed:  tx = -mx × (scale − 1)
      function updatePosition(e) {
        var rect = container.getBoundingClientRect();
        overlayImg.style.transform =
          'translate(' +
          (-(e.clientX - rect.left) * (activeScale - 1)).toFixed(2) + 'px,' +
          (-(e.clientY - rect.top)  * (activeScale - 1)).toFixed(2) + 'px)';
      }

      // ── Zoom in / out ─────────────────────────────────────────────────────
      function showZoom() {
        pending               = false;
        zoomed                = true;
        overlay.style.display = 'block';
        container.classList.add('ocs-zoomed');
      }

      function zoomIn(e) {
        // Read scale once — reused for this entire zoom session
        var raw = getComputedStyle(container).getPropertyValue('--ocs-zoom-scale').trim();
        activeScale = parseFloat(raw) > 1 ? parseFloat(raw) : 2.5;

        // Mirror object-fit / object-position from the original image
        var cs = getComputedStyle(img);
        overlayImg.style.objectFit      = cs.objectFit      || 'cover';
        overlayImg.style.objectPosition = cs.objectPosition || 'center center';

        // Size the overlay img at the actual zoomed dimensions
        overlayImg.style.width  = (activeScale * 100) + '%';
        overlayImg.style.height = (activeScale * 100) + '%';

        if (overlayImg.getAttribute('src') !== highResSrc) {
          overlayImg.setAttribute('src', highResSrc);
        }

        updatePosition(e);
        pending = true;

        // Use load/complete — more reliable than decode() for hidden elements
        if (overlayImg.complete && overlayImg.naturalWidth > 0) {
          showZoom();
        } else {
          var onReady = function () {
            overlayImg.removeEventListener('load',  onReady);
            overlayImg.removeEventListener('error', onReady);
            if (pending) showZoom();
          };
          overlayImg.addEventListener('load',  onReady);
          overlayImg.addEventListener('error', onReady);
        }
      }

      function zoomOut() {
        pending               = false;
        zoomed                = false;
        overlay.style.display = 'none';
        container.classList.remove('ocs-zoomed');
      }

      // ── Events ────────────────────────────────────────────────────────────
      container.addEventListener('click', function (e) {
        // A touch drag can emit a compatibility click after touchend. Ignore it
        // so panning does not immediately close the zoom the shopper is using.
        if (Date.now() < suppressClickUntil) {
          e.preventDefault();
          return;
        }

        if (zoomed || pending) zoomOut();
        else zoomIn(e);
      });

      // Keyboard: Enter/Space activate zoom for keyboard users (role="button")
      container.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (zoomed || pending) {
            zoomOut();
          } else {
            // Synthesise a pointer event at container centre for zoom positioning
            var rect = container.getBoundingClientRect();
            zoomIn({ clientX: rect.left + rect.width / 2, clientY: rect.top + rect.height / 2 });
          }
        }
      });

      container.addEventListener('mousemove', function (e) {
        if (zoomed) updatePosition(e);
      });

      container.addEventListener('touchstart', function (e) {
        if (!zoomed || !e.touches.length) return;
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        touchMoved = false;
      }, { passive: true });

      container.addEventListener('touchmove', function (e) {
        if (!zoomed || !e.touches.length) return;
        var touch = e.touches[0];
        if (Math.abs(touch.clientX - touchStartX) > 4 || Math.abs(touch.clientY - touchStartY) > 4) {
          touchMoved = true;
        }
        if (!touchMoved) return;

        e.preventDefault();
        updatePosition(touch);
      }, { passive: false });

      container.addEventListener('touchend', function () {
        if (touchMoved) suppressClickUntil = Date.now() + 500;
        touchMoved = false;
      });

      container.addEventListener('touchcancel', function () {
        touchMoved = false;
      });

      container.addEventListener('mouseleave', function () {
        if (zoomed || pending) zoomOut();
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGalleryZoom);
  } else {
    initGalleryZoom();
  }
})();
